import { useMemo, useState } from "react";
import './ManagementKPIDashboard.css'; // Importiere die CSS-Datei

// Typdefinitionen für KPI-Daten
type KPIPeriod = "current_quarter" | "last_quarter" | "year_to_date";

type RequestType = "premium" | "free";

type RequestStatus = "open" | "in_progress" | "completed" | "cancelled";

type Employee = {
  id: string;
  name: string;
  avatar: string;
  role: string;
  team: string;
};

type RequestSource = {
  id: string;
  name: string;
  channel: string;
  requestCount: number;
  conversionRate: number;
  revenue: number;
};

type KPIData = {
  period: KPIPeriod;
  totalRequests: number;
  premiumRequests: number;
  freeRequests: number;
  avgProcessingTime: number; // in Stunden
  medianProcessingTime: number; // in Stunden
  slaViolations: number;
  conversionRate: number; // in Prozent
  totalRevenue: number; // in Euro
  completedContracts: number;
  totalCommission: number; // in Euro
  openRequests: number;
  inProgressRequests: number;
  nearCompletionRequests: number;
  documentationRate: number; // in Prozent
  complaints: number;
  cancellations: number;
  crossSellingRevenue: number;
  additionalNeedsReported: number;
  forecastedRevenue: number;
  monthlyTrend: Array<{
    month: string;
    requests: number;
    revenue: number;
  }>;
};

type EmployeePerformance = {
  employee: Employee;
  processedRequests: number;
  successRate: number;
  avgProcessingTime: number;
  commission: number;
  additionalNeeds: number;
  workload: "low" | "medium" | "high" | "overloaded";
};

type CapacityData = {
  openRequests: number;
  inProgressRequests: number;
  nearCompletionRequests: number;
  bottlenecks: string[];
  warnings: Array<{
    id: string;
    message: string;
    severity: "low" | "medium" | "high";
    daysStale: number;
  }>;
};

type QualityData = {
  documentationRate: number;
  complaints: number;
  cancellations: number;
  auditTrail: Array<{
    date: string;
    employee: string;
    action: string;
    status: string;
  }>;
};

// ---- Dummy Daten für das letzte Quartal mit stark positivem Trend ----
const DUMMY_EMPLOYEES: Employee[] = [
  { id: "emp_1", name: "Max Mustermann", avatar: "👨‍💼", role: "Senior Manager", team: "Team A" },
  { id: "emp_2", name: "Erika Schmidt", avatar: "👩‍💼", role: "Manager", team: "Team B" },
  { id: "emp_3", name: "Hans Weber", avatar: "👨‍💼", role: "Junior Manager", team: "Team A" },
  { id: "emp_4", name: "Anna Müller", avatar: "👩‍💼", role: "Senior Manager", team: "Team B" },
  { id: "emp_5", name: "Klaus Bauer", avatar: "👨‍💼", role: "Manager", team: "Team C" },
];

const DUMMY_REQUEST_SOURCES: RequestSource[] = [
  { id: "source_1", name: "Verbund Pflegehilfe", channel: "Pflegeberatung", requestCount: 245, conversionRate: 18.5, revenue: 67800 },
  { id: "source_2", name: "Anfragen-Manager", channel: "Pflegeberatung", requestCount: 189, conversionRate: 15.2, revenue: 32100 },
  { id: "source_3", name: "Entlass-Manager", channel: "Plattform", requestCount: 156, conversionRate: 32.8, revenue: 15300 },
  { id: "source_4", name: "Direktanfragen", channel: "Direct", requestCount: 134, conversionRate: 28.9, revenue: 34200 },
  { id: "source_5", name: "Newsletter", channel: "Email", requestCount: 98, conversionRate: 12.3, revenue: 18900 },
];

const CURRENT_QUARTER_KPI: KPIData = {
  period: "current_quarter",
  totalRequests: 822,
  premiumRequests: 543,
  freeRequests: 279,
  avgProcessingTime: 24.5,
  medianProcessingTime: 18.2,
  slaViolations: 12,
  conversionRate: 22.8,
  totalRevenue: 221500,
  completedContracts: 187,
  totalCommission: 44300,
  openRequests: 45,
  inProgressRequests: 78,
  nearCompletionRequests: 23,
  documentationRate: 94.2,
  complaints: 8,
  cancellations: 15,
  crossSellingRevenue: 6780,
  additionalNeedsReported: 134,
  forecastedRevenue: 285000,
  monthlyTrend: [
    { month: "Jul", requests: 245, revenue: 58000 },
    { month: "Aug", requests: 267, revenue: 72000 },
    { month: "Sep", requests: 310, revenue: 91500 },
  ]
};

const LAST_QUARTER_KPI: KPIData = {
  period: "last_quarter",
  totalRequests: 689,
  premiumRequests: 412,
  freeRequests: 277,
  avgProcessingTime: 31.2,
  medianProcessingTime: 26.8,
  slaViolations: 28,
  conversionRate: 18.5,
  totalRevenue: 165800,
  completedContracts: 142,
  totalCommission: 33160,
  openRequests: 52,
  inProgressRequests: 89,
  nearCompletionRequests: 18,
  documentationRate: 87.5,
  complaints: 15,
  cancellations: 28,
  crossSellingRevenue: 42300,
  additionalNeedsReported: 98,
  forecastedRevenue: 198000,
  monthlyTrend: [
    { month: "Apr", requests: 198, revenue: 42000 },
    { month: "Mai", requests: 223, revenue: 58000 },
    { month: "Jun", requests: 268, revenue: 65800 },
  ]
};

const EMPLOYEE_PERFORMANCES: EmployeePerformance[] = [
  {
    employee: DUMMY_EMPLOYEES[0],
    processedRequests: 89,
    successRate: 94.3,
    avgProcessingTime: 22.1,
    commission: 12400,
    additionalNeeds: 34,
    workload: "medium"
  },
  {
    employee: DUMMY_EMPLOYEES[1],
    processedRequests: 76,
    successRate: 91.2,
    avgProcessingTime: 25.8,
    commission: 10800,
    additionalNeeds: 28,
    workload: "medium"
  },
  {
    employee: DUMMY_EMPLOYEES[2],
    processedRequests: 54,
    successRate: 87.5,
    avgProcessingTime: 28.9,
    commission: 7600,
    additionalNeeds: 22,
    workload: "low"
  },
  {
    employee: DUMMY_EMPLOYEES[3],
    processedRequests: 82,
    successRate: 96.1,
    avgProcessingTime: 20.5,
    commission: 11500,
    additionalNeeds: 31,
    workload: "high"
  },
  {
    employee: DUMMY_EMPLOYEES[4],
    processedRequests: 67,
    successRate: 89.7,
    avgProcessingTime: 26.2,
    commission: 9200,
    additionalNeeds: 19,
    workload: "medium"
  },
];

const CAPACITY_DATA: CapacityData = {
  openRequests: 45,
  inProgressRequests: 78,
  nearCompletionRequests: 23,
  bottlenecks: ["Team B - Hohe Auslastung", "Premium-Anfragen - Verzögerungen"],
  warnings: [
    { id: "warn_1", message: "5 Anfragen seit 3 Tagen unbearbeitet", severity: "high", daysStale: 3 },
    { id: "warn_2", message: "8 Anfragen verspätet", severity: "medium", daysStale: 2 },
    { id: "warn_3", message: "Dokumentation bei 3 Anfragen unvollständig", severity: "low", daysStale: 1 },
  ]
};

const QUALITY_DATA: QualityData = {
  documentationRate: 94.2,
  complaints: 8,
  cancellations: 15,
  auditTrail: [
    { date: "2024-09-15", employee: "Max Mustermann", action: "Vertrag abgeschlossen", status: "Erfolgreich" },
    { date: "2024-09-14", employee: "Erika Schmidt", action: "Dokumentation vervollständigt", status: "Erfolgreich" },
    { date: "2024-09-13", employee: "Anna Müller", action: "Reklamation bearbeitet", status: "In Bearbeitung" },
    { date: "2024-09-12", employee: "Hans Weber", action: "Cross-Selling angeboten", status: "Erfolgreich" },
  ]
};

// ---- UI Atoms ----
function KPICard({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon,
  color = "blue",
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: string;
  trendValue?: string;
  icon?: React.ReactNode;
  color?: "blue" | "green" | "orange" | "red" | "purple";
}) {
  const getTrendIcon = () => {
    switch (trend) {
      case "up": return <i className="fa-solid fa-arrow-up" />;
      case "down": return <i className="fa-solid fa-arrow-down" />;
      default: return <i className="fa-solid fa-arrow-right" />;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case "up": return "text-green-600";
      case "down": return "text-red-600";
      default: return "text-gray-600";
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case "green": return "border-green-200 bg-green-50";
      case "orange": return "border-orange-200 bg-orange-50";
      case "red": return "border-red-200 bg-red-50";
      case "purple": return "border-purple-200 bg-purple-50";
      default: return "border-blue-200 bg-blue-50";
    }
  };

  return (
    <div className={`kpi-card ${getColorClasses()}`}>
      <div className="kpi-card-header">
        <div className="kpi-card-title">
          {icon && <span className="kpi-card-icon">{icon}</span>}
          <span className="kpi-card-label">{title}</span>
        </div>
        {trend && (
          <div className={`kpi-card-trend ${getTrendColor()}`}>
            <span className="trend-icon">{getTrendIcon()}</span>
            <span className="trend-value"> {trendValue}</span>
          </div>
        )}
      </div>
      <div className="kpi-card-value">{value}</div>
      {subtitle && <div className="kpi-card-subtitle">{subtitle}</div>}
    </div>
  );
}

function EmployeePerformanceCard({
  performance,
  rank,
}: {
  performance: EmployeePerformance;
  rank: number;
}) {
  const getWorkloadColor = () => {
    switch (performance.workload) {
      case "low": return "text-green-600 bg-green-100";
      case "medium": return "text-blue-600 bg-blue-100";
      case "high": return "text-orange-600 bg-orange-100";
      case "overloaded": return "text-red-600 bg-red-100";
    }
  };

  const getWorkloadIcon = () => {
    switch (performance.workload) {
      case "low": return <i className="fa-solid fa-droplet" />;
      case "medium": return <i className="fa-solid fa-bolt" />;
      case "high": return <i className="fa-solid fa-fire" />;
      case "overloaded": return <i className="fa-solid fa-triangle-exclamation" />;
    }
  };

  return (
    <div className="employee-performance-card">
      <div className="employee-card-header">
        <div className="employee-rank">#{rank}</div>
        <div className="employee-info">
          <div className="employee-avatar">{performance.employee.avatar}</div>
          <div className="employee-details">
            <div className="employee-name">{performance.employee.name}</div>
            <div className="employee-role">{performance.employee.role} • {performance.employee.team}</div>
          </div>
        </div>
        <div className={`workload-badge ${getWorkloadColor()}`}>
          <span className="workload-icon">{getWorkloadIcon()}</span>
          <span className="workload-text">{performance.workload}</span>
        </div>
      </div>
      
      <div className="employee-metrics">
        <div className="metric-item">
          <span className="metric-label">Bearbeitet</span>
          <span className="metric-value">{performance.processedRequests}</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Erfolgsquote</span>
          <span className="metric-value">{performance.successRate}%</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">⌀ Zeit</span>
          <span className="metric-value">{performance.avgProcessingTime}h</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Zusatzbedarf</span>
          <span className="metric-value">€{performance.commission.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function WarningCard({
  warning,
}: {
  warning: CapacityData["warnings"][0];
}) {
  const getSeverityColor = () => {
    switch (warning.severity) {
      case "low": return "border-yellow-200 bg-yellow-50";
      case "medium": return "border-orange-200 bg-orange-50";
      case "high": return "border-red-200 bg-red-50";
    }
  };

  const getSeverityIcon = () => {
    switch (warning.severity) {
      case "low": return <i className="fa-solid fa-triangle-exclamation" />;
      case "medium": return <i className="fa-solid fa-diamond-exclamation" />;
      case "high": return <i className="fa-solid fa-bomb" />;
    }
  };

  return (
    <div className={`warning-card ${getSeverityColor()}`}>
      <div className="warning-header">
        <span className="warning-icon">{getSeverityIcon()}</span>
        <span className="warning-severity">{warning.severity.toUpperCase()}</span>
        <span className="warning-days">{warning.daysStale} Tage</span>
      </div>
      <div className="warning-message">{warning.message}</div>
    </div>
  );
}

// ---- Hauptkomponente ----
export default function ManagementKPIDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState<KPIPeriod>("current_quarter");
  const [activeTab, setActiveTab] = useState<"overview" | "employees" | "capacity" | "quality">("overview");

  // KPI-Daten basierend auf ausgewähltem Zeitraum
  const kpiData = useMemo(() => {
    return selectedPeriod === "current_quarter" ? CURRENT_QUARTER_KPI : LAST_QUARTER_KPI;
  }, [selectedPeriod]);

  // Vergleichsdaten für Trend-Berechnung
  const comparisonData = useMemo(() => {
    return selectedPeriod === "current_quarter" ? LAST_QUARTER_KPI : CURRENT_QUARTER_KPI;
  }, [selectedPeriod]);

  // Trend-Berechnung
  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return { trend: "stable" as const, value: "0%" };
    const change = ((current - previous) / previous) * 100;
    return {
      trend: (change > 0 ? "up" : change < 0 ? "down" : "stable") as "up" | "down" | "stable",
      value: `${Math.abs(change).toFixed(1)}%`
    };
  };

  // Render Funktionen für verschiedene Tabs
  function renderOverview() {
    const requestTrend = calculateTrend(kpiData.totalRequests, comparisonData.totalRequests);
    const conversionTrend = calculateTrend(kpiData.conversionRate, comparisonData.conversionRate);
    const revenueTrend = calculateTrend(kpiData.totalRevenue, comparisonData.totalRevenue);
    const processingTrend = calculateTrend(kpiData.avgProcessingTime, comparisonData.avgProcessingTime);
    
    // Umgekehrter Trend für Bearbeitungszeit (weniger ist besser)
    const processingTimeTrend = {
      trend: processingTrend.trend === "up" ? "down" : processingTrend.trend === "down" ? "up" : "stable",
      value: processingTrend.value
    };

    return (
      <div className="overview-tab">
        {/* KPI Übersicht */}
        <div className="kpi-section">
          <h3 className="section-title"><i className="fa-solid fa-chart-line" /> KPI-Übersicht</h3>
          <div className="kpi-grid">
            <KPICard
              title="Eingegangene Anfragen"
              value={kpiData.totalRequests}
              subtitle={`Premium: ${kpiData.premiumRequests} | Kostenlos: ${kpiData.freeRequests}`}
              trend={requestTrend.trend}
              trendValue={requestTrend.value}
              icon={<i className="fa-solid fa-inbox" />}
              color="blue"
            />
            <KPICard
              title="Conversion-Rate"
              value={`${kpiData.conversionRate}%`}
              subtitle={`${kpiData.completedContracts} Verträge abgeschlossen`}
              trend={conversionTrend.trend}
              trendValue={conversionTrend.value}
              icon={<i className="fa-solid fa-bullseye" />}
              color="green"
            />
            <KPICard
              title="Gesamtumsatz"
              value={`€${kpiData.totalRevenue.toLocaleString()}`}
              subtitle={`Provisionen: €${kpiData.totalCommission.toLocaleString()}`}
              trend={revenueTrend.trend}
              trendValue={revenueTrend.value}
              icon={<i className="fa-solid fa-euro-sign" />}
              color="purple"
            />
            <KPICard
              title="⌀ Bearbeitungszeit"
              value={`${kpiData.avgProcessingTime}h`}
              subtitle={`Median: ${kpiData.medianProcessingTime}h`}
              trend={processingTimeTrend.trend}
              trendValue={processingTimeTrend.value}
              icon={<i className="fa-solid fa-clock" />}
              color="orange"
            />
          </div>
        </div>

        {/* Top Quellen */}
        <div className="sources-section">
          <h3 className="section-title"><i className="fa-solid fa-globe" /> Top-Anfragequellen</h3>
          <div className="sources-grid">
            {DUMMY_REQUEST_SOURCES.slice(0, 4).map((source, index) => (
              <div key={source.id} className="source-card">
                <div className="source-header">
                  <div className="source-rank">#{index + 1}</div>
                  <div className="source-name">{source.name}</div>
                  <div className="source-channel">{source.channel}</div>
                </div>
                <div className="source-metrics">
                  <div className="source-metric">
                    <span className="metric-label">Anfragen</span>
                    <span className="metric-value">{source.requestCount}</span>
                  </div>
                  <div className="source-metric">
                    <span className="metric-label">Umsatz</span>
                    <span className="metric-value">€{source.revenue.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cross-Selling & Forecast */}
        <div className="forecast-section">
          <h3 className="section-title"><i className="fa-solid fa-crystal-ball" /> Cross-Selling & Forecast</h3>
          <div className="forecast-grid">
            <KPICard
              title="Cross-Selling Umsatz"
              value={`€${kpiData.crossSellingRevenue.toLocaleString()}`}
              subtitle={`${kpiData.additionalNeedsReported} Zusatzbedarfe gemeldet`}
              trend="up"
              trendValue="+60.3%"
              icon={<i className="fa-solid fa-gem" />}
              color="green"
            />
            <KPICard
              title="Prognose Pipeline"
              value={`€${kpiData.forecastedRevenue.toLocaleString()}`}
              subtitle={`Für nächstes Quartal prognostiziert`}
              trend="up"
              trendValue="+43.9%"
              icon={<i className="fa-solid fa-chart-line" />}
              color="purple"
            />
          </div>
        </div>

        {/* Monatlicher Trend */}
        <div className="trend-section">
          <h3 className="section-title"><i className="fa-solid fa-chart-column" /> Monatlicher Trend</h3>
          <div className="trend-chart">
            <div className="trend-bars">
              {kpiData.monthlyTrend.map((month, index) => (
                <div key={month.month} className="trend-bar-container">
                  <div className="trend-month">{month.month}</div>
                  <div className="trend-bar-wrapper">
                    <div 
                      className="trend-bar requests"
                      style={{ height: `${(month.requests / 350) * 100}%` }}
                    >
                      <span className="trend-bar-value">{month.requests}</span>
                    </div>
                    <div 
                      className="trend-bar revenue"
                      style={{ height: `${(month.revenue / 100000) * 100}%` }}
                    >
                      <span className="trend-bar-value">€{(month.revenue / 1000).toFixed(0)}k</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="trend-legend">
              <div className="legend-item">
                <span className="legend-color requests"></span>
                <span className="legend-label">Anfragen</span>
              </div>
              <div className="legend-item">
                <span className="legend-color revenue"></span>
                <span className="legend-label">Umsatz (€k)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderEmployees() {
    // Sortiere Mitarbeiter nach Performance
    const sortedEmployees = [...EMPLOYEE_PERFORMANCES].sort((a, b) => {
      // Primär nach Anzahl bearbeiteter Anfragen
      if (a.processedRequests !== b.processedRequests) {
        return b.processedRequests - a.processedRequests;
      }
      // Sekundär nach Erfolgsquote
      return b.successRate - a.successRate;
    });

    return (
      <div className="employees-tab">
        <div className="employees-header">
          <h3 className="section-title"><i className="fa-solid fa-users" /> Mitarbeiter Performance</h3>
          <div className="employees-summary">
            <div className="summary-item">
              <span className="summary-label">Aktive Mitarbeiter</span>
              <span className="summary-value">{EMPLOYEE_PERFORMANCES.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">⌀ Bearbeitete Anfragen</span>
              <span className="summary-value">
                {Math.round(EMPLOYEE_PERFORMANCES.reduce((sum, emp) => sum + emp.processedRequests, 0) / EMPLOYEE_PERFORMANCES.length)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Gesamtprovisionen</span>
              <span className="summary-value">
                €{EMPLOYEE_PERFORMANCES.reduce((sum, emp) => sum + emp.commission, 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="employees-grid">
          {sortedEmployees.map((performance, index) => (
            <EmployeePerformanceCard
              key={performance.employee.id}
              performance={performance}
              rank={index + 1}
            />
          ))}
        </div>

        {/* Performance Heatmap Info */}
        <div className="heatmap-info">
          <h4><i className="fa-solid fa-fire" /> Workload Heatmap</h4>
          <div className="workload-legend">
            <div className="workload-legend-item">
              <span className="workload-legend-color low"></span>
              <span className="workload-legend-text">Geringe Auslastung</span>
            </div>
            <div className="workload-legend-item">
              <span className="workload-legend-color medium"></span>
              <span className="workload-legend-text">Normale Auslastung</span>
            </div>
            <div className="workload-legend-item">
              <span className="workload-legend-color high"></span>
              <span className="workload-legend-text">Hohe Auslastung</span>
            </div>
            <div className="workload-legend-item">
              <span className="workload-legend-color overloaded"></span>
              <span className="workload-legend-text">Überlastet</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderCapacity() {
    return (
      <div className="capacity-tab">
        {/* Kapazitätsübersicht */}
        <div className="capacity-overview">
          <h3 className="section-title"><i className="fa-solid fa-chart-pie" /> Kapazitätsübersicht</h3>
          <div className="capacity-grid">
            <KPICard
              title="Offene Anfragen"
              value={CAPACITY_DATA.openRequests}
              subtitle="Warten auf Bearbeitung"
              icon={<i className="fa-solid fa-hourglass-half" />}
              color="orange"
            />
            <KPICard
              title="In Bearbeitung"
              value={CAPACITY_DATA.inProgressRequests}
              subtitle="Aktiv bearbeitet"
              icon={<i className="fa-solid fa-rotate" />}
              color="blue"
            />
            <KPICard
              title="Vor Abschluss"
              value={CAPACITY_DATA.nearCompletionRequests}
              subtitle="Kurz vor der Fertigstellung"
              icon={<i className="fa-solid fa-check-circle" />}
              color="green"
            />
            <KPICard
              title="Gesamt offen"
              value={CAPACITY_DATA.openRequests + CAPACITY_DATA.inProgressRequests + CAPACITY_DATA.nearCompletionRequests}
              subtitle="Summe aller aktiven Anfragen"
              icon={<i className="fa-solid fa-clipboard-list" />}
              color="purple"
            />
          </div>
        </div>

        {/* Engpässe */}
        <div className="bottlenecks-section">
          <h3 className="section-title"><i className="fa-solid fa-triangle-exclamation" /> Identifizierte Engpässe</h3>
          <div className="bottlenecks-list">
            {CAPACITY_DATA.bottlenecks.map((bottleneck, index) => (
              <div key={index} className="bottleneck-item">
                <span className="bottleneck-icon"><i className="fa-solid fa-construction" /></span>
                <span className="bottleneck-text">{bottleneck}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Frühwarnungen */}
        <div className="warnings-section">
          <h3 className="section-title"><i className="fa-solid fa-bomb" /> Frühwarnungen</h3>
          <div className="warnings-grid">
            {CAPACITY_DATA.warnings.map((warning) => (
              <WarningCard key={warning.id} warning={warning} />
            ))}
          </div>
        </div>

        {/* Kapazitätsauslastung */}
        <div className="utilization-section">
          <h3 className="section-title"><i className="fa-solid fa-chart-area" /> Kapazitätsauslastung</h3>
          <div className="utilization-chart">
            <div className="utilization-bars">
              <div className="utilization-bar">
                <div className="utilization-label">Offen</div>
                <div className="utilization-progress">
                  <div 
                    className="utilization-fill orange"
                    style={{ width: `${(CAPACITY_DATA.openRequests / 200) * 100}%` }}
                  ></div>
                </div>
                <div className="utilization-value">{CAPACITY_DATA.openRequests}</div>
              </div>
              <div className="utilization-bar">
                <div className="utilization-label">In Bearbeitung</div>
                <div className="utilization-progress">
                  <div 
                    className="utilization-fill blue"
                    style={{ width: `${(CAPACITY_DATA.inProgressRequests / 200) * 100}%` }}
                  ></div>
                </div>
                <div className="utilization-value">{CAPACITY_DATA.inProgressRequests}</div>
              </div>
              <div className="utilization-bar">
                <div className="utilization-label">Vor Abschluss</div>
                <div className="utilization-progress">
                  <div 
                    className="utilization-fill green"
                    style={{ width: `${(CAPACITY_DATA.nearCompletionRequests / 200) * 100}%` }}
                  ></div>
                </div>
                <div className="utilization-value">{CAPACITY_DATA.nearCompletionRequests}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderQuality() {
    const qualityTrend = calculateTrend(kpiData.documentationRate, comparisonData.documentationRate);
    const complaintsTrend = calculateTrend(kpiData.complaints, comparisonData.complaints);
    
    // Umgekehrter Trend für Beschwerden (weniger ist besser)
    const complaintsTrendReversed = {
      trend: complaintsTrend.trend === "up" ? "down" : complaintsTrend.trend === "down" ? "up" : "stable",
      value: complaintsTrend.value
    };

    return (
      <div className="quality-tab">
        {/* Qualität KPIs */}
        <div className="quality-kpi-section">
          <h3 className="section-title"><i className="fa-solid fa-clipboard-check" /> Qualität & Compliance</h3>
          <div className="quality-kpi-grid">
            <KPICard
              title="Dokumentationsquote"
              value={`${kpiData.documentationRate}%`}
              subtitle="Vollständige Dokumentation"
              trend={qualityTrend.trend}
              trendValue={qualityTrend.value}
              icon={<i className="fa-solid fa-file-lines" />}
              color="green"
            />
            <KPICard
              title="Reklamationen"
              value={kpiData.complaints}
              subtitle="Beschwerden insgesamt"
              trend={complaintsTrendReversed.trend}
              trendValue={complaintsTrendReversed.value}
              icon={<i className="fa-solid fa-triangle-exclamation" />}
              color="orange"
            />
            <KPICard
              title="Kündigungen"
              value={kpiData.cancellations}
              subtitle="Vertragskündigungen"
              trend="down"
              trendValue="-46.4%"
              icon={<i className="fa-solid fa-xmark" />}
              color="red"
            />
            <KPICard
              title="Audit-Trail"
              value="100%"
              subtitle="Vollständige Nachverfolgung"
              trend="stable"
              trendValue="0%"
              icon={<i className="fa-solid fa-magnifying-glass" />}
              color="blue"
            />
          </div>
        </div>

        {/* Audit Trail */}
        <div className="audit-section">
          <h3 className="section-title"><i className="fa-solid fa-magnifying-glass-chart" /> Audit Trail</h3>
          <div className="audit-table">
            <div className="audit-table-header">
              <div className="audit-header-cell">Datum</div>
              <div className="audit-header-cell">Mitarbeiter</div>
              <div className="audit-header-cell">Aktion</div>
              <div className="audit-header-cell">Status</div>
            </div>
            <div className="audit-table-body">
              {QUALITY_DATA.auditTrail.map((entry, index) => (
                <div key={index} className="audit-table-row">
                  <div className="audit-cell">{entry.date}</div>
                  <div className="audit-cell">{entry.employee}</div>
                  <div className="audit-cell">{entry.action}</div>
                  <div className="audit-cell">
                    <span className={`audit-status ${entry.status === "Erfolgreich" ? "success" : "pending"}`}>
                      {entry.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Qualitätsmetriken */}
        <div className="quality-metrics-section">
          <h3 className="section-title"><i className="fa-solid fa-chart-bar" /> Qualitätsmetriken</h3>
          <div className="quality-metrics-grid">
            <div className="quality-metric-card">
              <div className="quality-metric-header">
              <span className="quality-metric-icon"><i className="fa-solid fa-check-circle" /></span>
                <span className="quality-metric-title">Erfolgsrate</span>
              </div>
              <div className="quality-metric-value">94.2%</div>
              <div className="quality-metric-subtitle">Erfolgreich abgeschlossene Anfragen</div>
            </div>
            
            <div className="quality-metric-card">
              <div className="quality-metric-header">
              <span className="quality-metric-icon"><i className="fa-solid fa-clock" /></span>
                <span className="quality-metric-title">SLA-Einhaltung</span>
              </div>
              <div className="quality-metric-value">98.5%</div>
              <div className="quality-metric-subtitle">Service-Level-Vertrag eingehalten</div>
            </div>
            
            <div className="quality-metric-card">
              <div className="quality-metric-header">
              <span className="quality-metric-icon"><i className="fa-solid fa-rotate" /></span>
                <span className="quality-metric-title">Wiederholungsrate</span>
              </div>
              <div className="quality-metric-value">12.3%</div>
              <div className="quality-metric-subtitle">Wiederkehrende Kunden</div>
            </div>
            
            <div className="quality-metric-card">
              <div className="quality-metric-header">
              <span className="quality-metric-icon"><i className="fa-solid fa-gem" /></span>
                <span className="quality-metric-title">Zufriedenheit</span>
              </div>
              <div className="quality-metric-value">4.8/5</div>
              <div className="quality-metric-subtitle">Kundenbewertung</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Haupt-Render ----
  return (
    <section className="management-kpi-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="dashboard-title-section">
          <h1 className="dashboard-title">Management Dashboard</h1>
          <p className="dashboard-subtitle">
            Steuerungsinstrument für das Pflege-Management
          </p>
        </div>
        
        {/* Zeitraum-Auswahl */}
        <div className="period-selector">
          <select
            className="period-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as KPIPeriod)}
          >
            <option value="current_quarter">Aktuelles Quartal (Q3 2024)</option>
            <option value="last_quarter">Letztes Quartal (Q2 2024)</option>
            <option value="year_to_date">Jahr bis heute</option>
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <div className="tab-navigation-2">
          {[
            { id: "overview", label: "Übersicht", icon: <i className="fa-solid fa-chart-line" /> },
            { id: "employees", label: "Mitarbeiter", icon: <i className="fa-solid fa-users" /> },
            { id: "capacity", label: "Kapazität", icon: <i className="fa-solid fa-chart-pie" /> },
            { id: "quality", label: "Qualität", icon: <i className="fa-solid fa-clipboard-check" /> }
          ].map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id as any)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "employees" && renderEmployees()}
        {activeTab === "capacity" && renderCapacity()}
        {activeTab === "quality" && renderQuality()}
      </div>

      {/* Zusammenfassung Footer */}
      <div className="dashboard-footer">
        <div className="footer-summary">
          <div className="summary-highlight">
            <span className="highlight-icon"><i className="fa-solid fa-rocket" /></span>
            <span className="highlight-text">
              Starkes Wachstum: +{calculateTrend(kpiData.totalRevenue, comparisonData.totalRevenue).value} Umsatzsteigerung zum Vorquartal
            </span>
          </div>
          <div className="summary-details">
            <span className="detail-item">
              <span className="detail-icon"><i className="fa-solid fa-calendar" /></span>
              Letzte Aktualisierung: {new Date().toLocaleDateString('de-DE')}
            </span>
            <span className="detail-item">
              <span className="detail-icon"><i className="fa-solid fa-sync" /></span>
              Live-Daten
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
